<template>
  <div class="row p-1">
    <div class="col-5">
      <h2>Payment Page</h2>
      <p class="text-muted">
        An online payment form is an excellent way to give your patients the
        ability to self-service an outstanding balance. Link to a branded
        payment page or embed the payment form directly on your website.
      </p>

      <p class="text-muted">
        You can also link the payment page by allowing customers to scan the QR
        code provided.
      </p>
    </div>
    <div class="col-7 pl-6">
      <b-card class="border shadow-sm">
        <h3>Branded Payment Page</h3>
        <p class="text-muted">
          Please enter the name you would like to use for your payment page URL.
        </p>

        <b-input-group class="mt-3" :prepend="patientPortalUrl + '/'">
          <b-form-input
            v-model="patientHostName"
            rules="required"
            name="Host Name"
            v-on:keypress="isLetter($event)"
            v-debounce:300ms="submit"
          ></b-form-input>
          <b-input-group-append>
            <b-button
              class="btn-outline-primary shadow-none"
              type="button"
              id="copy-button"
              data-placement="button"
              @click="copy"
              >Copy</b-button
            >
          </b-input-group-append>
        </b-input-group>

        <div class="light-alert mb-0 mt-4">
          <i class="fa-solid fa-triangle-exclamation"></i>Changing the payment
          page URL will result in the old link no longer working.
        </div>

        <p class="text-muted pt-4">
          Download and place this QR code on statements, web pages, or any other
          source you'd like to link to your branded payment page.
        </p>
        <img :src="qrCode" style="width:160px" /><br /><br />

        <a href="#" @click.prevent="downloadQrCode"
          ><i class="fa-solid fa-cloud-arrow-down"></i>&nbsp;&nbsp;Download</a
        >
      </b-card>

      <b-card class="border shadow-sm">
        <h3>Embed Code</h3>
        <p class="text-muted">
          Copy and paste the following code to embed the payment form directly
          on your website.<br />
        </p>

        <div class="link-background">
          <code>
            &lt;iframe src=&quot;{{
              patientPortalUrl + "/" + patientHostName + "/i"
            }}&quot; width=&quot;100%&quot; height=&quot;1275&quot;
            scrolling=&quot;no&quot; allowfullscreen frameborder=&quot;0&quot;
            marginwidth=&quot;0&quot;
            marginheight=&quot;0&quot;&gt;&lt;/iframe&gt;
          </code>
        </div>
      </b-card>

      <b-card class="border shadow-sm">
        <validation-observer ref="formValidator" v-slot="{ handleSubmit }">
          <b-form role="form" @submit.prevent="handleSubmit(submitNote)">
            <h3 class="pb-0 mb-0">Payment Page Header</h3>
            <p class="text-muted pb-1" style="max-width:550px">
              Customize the header that is shown at the top of the payment page.
            </p>

            <base-textarea
              v-model="paymentPageHeader"
              name="Description"
              rows="3"
            ></base-textarea>
            <b-button type="submit" block variant="primary" class="mt-3">
              Save
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>

      <b-card class="border shadow-sm">
        <validation-observer ref="formValidator" v-slot="{ handleSubmit }">
          <b-form role="form" @submit.prevent="handleSubmit(submitNote)">
            <h3 class="pb-0 mb-0">Custom Field</h3>
            <p class="text-muted pb-4" style="max-width:550px">
              Customize a field on your payment form that will help collect
              specific information to identify better who or what this payment
              is for.
            </p>

            <base-input
              v-model="paymentPageNoteLabel"
              rules="required"
              name="Title"
              label="Field Title"
            ></base-input>

            <base-textarea
              v-model="paymentPageNoteDetail"
              name="Description"
              label="Field Description"
              rows="3"
            ></base-textarea>

            <h4 class="pb-0 mb-0">Require Field</h4>
            <p class="text-muted" style="max-width:550px">
              Require that this field cannot be left blank.
            </p>
            <b-form-checkbox
              v-model="paymentPageRequireNote"
              switch
              size="lg"
              class="pb-3"
            ></b-form-checkbox>

            <b-button type="submit" block variant="primary" class="mt-3">
              Save Custom Field
            </b-button>
          </b-form></validation-observer
        >
      </b-card>

      <b-card class="border shadow-sm mb-0">
        <h3>Send Instructions</h3>
        <p class="text-muted">
          Email your payment page details with instructions on how to link or
          embed your payment page.
        </p>

        <validation-observer ref="formValidator" v-slot="{ handleSubmit }">
          <b-form role="form" @submit.prevent="handleSubmit(sendEmailSubmit)">
            <b-input-group class="mt-3">
              <b-form-input
                v-model="email"
                name="e-mail"
                placeholder="e.g. name@domain.com"
                rules="required|email"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  type="submit"
                  class="btn-outline-primary shadow-none"
                  :disabled="!email"
                  variant="primary"
                  >Send</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-form></validation-observer
        >
      </b-card>
    </div>
  </div>
</template>

<script>
export default {
  inject: ["systemContextService"],
  props: {
    company: null,
    bindPaymentPageSettings: null,
    updatePaymentPageSettings: null,
    sendPaymentPageEmail: null,
  },

  data() {
    return {
      sysname: null,
      patientHostName: this.company.patientHostName,
      patientPortalUrl: null,
      patientPortalUrl: null,
      paymentPageNoteLabel: null,
      paymentPageRequireNote: null,
      paymentPageHeader: null,
      email: null,
      more: false,
      local_saved: true,
      qrCode: null,
    };
  },
  async mounted() {
    this.sysname = this.systemContextService.getSystem().name;
    let r = await this.bindPaymentPageSettings();

    this.patientPortalUrl = r.patientPortalUrl;
    this.patientHostName = r.patientHostName;
    this.paymentPageHeader = r.paymentPageHeader;
    this.paymentPageNoteLabel = r.paymentPageNoteLabel;
    this.paymentPageRequireNote = r.paymentPageRequireNote;
    this.paymentPageNoteDetail = r.paymentPageNoteDetail;
    this.qrCode = r.qrCode;
    this.local_saved = true;
  },
  methods: {
    async submitNote() {
      if (await this.submit()) {
        this.$emitter.emit("success", {
          message: "Payment Page Information Saved",
        });
      }
    },
    downloadQrCode() {
      var link = document.createElement("a");

      document.body.appendChild(link); // for Firefox

      link.setAttribute("href", this.qrCode);
      link.setAttribute("download", "qrCode.jpeg");
      link.click();
    },
    isLetter(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if (/^[A-Za-z]+$/.test(char)) return true;
      // Match with regex
      else e.preventDefault(); // If not match, don't add to input text
    },
    copy() {
      var text = this.patientPortalUrl + "/" + this.patientHostName;

      navigator.clipboard.writeText(text).then(
        () => {
          this.$emitter.emit("success", { message: "Copied to clipboard" });
        },
        (err) => {
          this.$emitter.emit(
            "no-success",
            "Unable to copy to clipboard " + err.message
          );
        }
      );
    },
    async submit() {
      return await this.updatePaymentPageSettings({
        noProgress: true,
        companyId: this.company.id,
        patientHostName: this.patientHostName,
        paymentPageRequireNote: this.paymentPageRequireNote,
        paymentPageNoteLabel: this.paymentPageNoteLabel,
        paymentPageNoteDetail: this.paymentPageNoteDetail,
        paymentPageHeader: this.paymentPageHeader,
        email: this.email,
      });
    },
    async sendEmailSubmit() {
      var saved = await this.sendPaymentPageEmail({
        companyId: this.company.id,
        patientHostName: this.patientHostName,
        email: this.email,
      });

      if (saved) {
        this.$emitter.emit("success", {
          message: "E-mail sent with payment page instructions",
        });
      }
    },
  },
};
</script>

<style scoped>
.link-background {
  border-radius: 6px;
  background: #f6f9fc;
}
</style>
